import { useMemo, useState } from "react";
import { useRouter, useNavigate, createFileRoute } from "@tanstack/react-router";
import { useGetProfile } from "@/lib/api/auth/hooks";

// FSD imports
import { useNoticeCategories, useCreateNotice } from "@/features/notice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Combobox,
  ComboboxInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxList,
  ComboboxItem,
} from "@/components/ui/combobox";
import { DocEditor, EMPTY_DOC, isDocEmpty } from "@growing/editor";
import "@growing/editor/styles.css";
import { createDocAssetAdapter } from "@growing/api";
import { apiClient } from "@/lib/api/client";
import type { DocContent } from "@growing/types";
import { Header } from "@/components/shared/header";
import { PageContainer } from "@/components/shared/page-container";
import { toast } from "sonner";

function NewNoticePage() {
  const router = useRouter();
  const navigate = useNavigate();
  const [doc, setDoc] = useState<DocContent>(EMPTY_DOC);
  const [title, setTitle] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [isPinned, setIsPinned] = useState(false);

  const { data: profile } = useGetProfile();
  const userId = profile?._id || "";

  const { data: categories } = useNoticeCategories();
  const createNotice = useCreateNotice();

  // 본문 이미지 업로드/조회 구현 (presign → S3 직접 PUT → 조회 URL)
  const assetAdapter = useMemo(() => createDocAssetAdapter(apiClient), []);

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast.error("제목을 입력해주세요.");
      return;
    }
    if (!categoryId) {
      toast.error("카테고리를 선택해주세요.");
      return;
    }

    if (isDocEmpty(doc)) {
      toast.error("내용을 입력해주세요.");
      return;
    }

    try {
      await createNotice.mutateAsync({
        title,
        category: categoryId,
        doc,
        isPinned,
        createdBy: userId,
      });
      toast.success("공지사항이 작성되었습니다.");
      navigate({ to: "/notice" });
    } catch {
      toast.error("공지사항 작성에 실패했습니다.");
    }
  };

  return (
    <PageContainer>
      <Header title="새 공지사항 작성" />

      <div className="w-full">
        <div className="space-y-6">
          {/* 카테고리 선택 */}
          <div className="space-y-2">
            <Label htmlFor="category">카테고리</Label>
            <Combobox
              items={categories ?? []}
              value={categories?.find((c) => c._id === categoryId) ?? null}
              onValueChange={(category) => setCategoryId(category?._id ?? "")}
              itemToStringLabel={(c) => c.name}
              isItemEqualToValue={(a, b) => a._id === b._id}
            >
              <ComboboxInput placeholder="카테고리를 선택하세요" />
              <ComboboxContent>
                <ComboboxEmpty>결과 없음</ComboboxEmpty>
                <ComboboxList>
                  {(category) => (
                    <ComboboxItem key={category._id} value={category}>
                      {category.name}
                    </ComboboxItem>
                  )}
                </ComboboxList>
              </ComboboxContent>
            </Combobox>
          </div>

          {/* 제목 */}
          <div className="space-y-2">
            <Label htmlFor="title">제목</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="제목을 입력하세요"
            />
          </div>

          {/* 상단 고정 */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isPinned"
              checked={isPinned}
              onChange={(e) => setIsPinned(e.target.checked)}
              className="size-4"
            />
            <Label htmlFor="isPinned" className="cursor-pointer">
              상단 고정
            </Label>
          </div>

          {/* 에디터 */}
          <div className="space-y-2">
            <Label>내용</Label>
            <DocEditor
              adapter={assetAdapter}
              placeholder="본문을 입력하세요. 이미지는 붙여넣거나 끌어다 놓을 수 있습니다."
              onChange={({ doc: next }) => setDoc(next)}
              onError={(message) => toast.error(message)}
            />
          </div>

          {/* 버튼 */}
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => router.history.back()}>
              취소
            </Button>
            <Button onClick={handleSubmit} disabled={createNotice.isPending}>
              {createNotice.isPending ? "작성 중..." : "작성하기"}
            </Button>
          </div>
        </div>
      </div>
    </PageContainer>
  );
}

export const Route = createFileRoute("/_content/notice/new")({
  component: NewNoticePage,
});
